import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useConfig } from '../../context/ConfigContext';
import { documentsAPI, chatAPI, customerAPI } from '../../services/api';
import {
  DocumentIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';

// Custom icons to match Figma
const DocumentsIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
  </svg>
);

const ChatIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
  </svg>
);

const StorageIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
  </svg>
);

const WidgetIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
  </svg>
);

// PDF icon for documents
const PdfIcon = () => (
  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
    <span className="text-red-600 text-xs font-bold">PDF</span>
  </div>
);

export default function AdminDashboard() {
  const { config } = useConfig();
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);

  const { data: dashboard, isLoading: dashboardLoading } = useQuery('dashboard', customerAPI.getDashboard);
  const { data: documents } = useQuery('documents', documentsAPI.getDocuments);
  const { data: chatSessions } = useQuery('chatSessions', chatAPI.getChatSessions);

  const stats = [
    {
      name: 'Total Documents',
      value: dashboard?.stats?.document_count || documents?.total || 0,
      icon: DocumentsIcon,
      bgColor: 'bg-cleona-50',
      iconColor: 'text-cleona-600',
    },
    {
      name: 'Chat Messages',
      value: dashboard?.stats?.message_count || chatSessions?.length || 0,
      icon: ChatIcon,
      bgColor: 'bg-cleona-50',
      iconColor: 'text-cleona-600',
    },
    {
      name: 'Storage Used',
      value: `${Math.round((documents?.documents?.reduce((acc, doc) => acc + doc.file_size, 0) || 0) / 1024 / 1024)}MB`,
      icon: StorageIcon,
      bgColor: 'bg-amber-50',
      iconColor: 'text-amber-600',
    },
    {
      name: 'Website Chat Status',
      value: dashboard?.stats?.widget_enabled ? 'Active' : 'Inactive',
      icon: WidgetIcon,
      bgColor: 'bg-cleona-50',
      iconColor: 'text-cleona-600',
    },
  ];

  const recentDocuments = documents?.documents?.slice(0, 4) || [];
  const recentSessions = chatSessions?.slice(0, 4) || [];

  if (dashboardLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cleona-600"></div>
      </div>
    );
  }

  return (
    <div className="flex gap-6">
      {/* Main content */}
      <div className="flex-1 space-y-6">
        {/* Welcome section */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-arsenal">
            Welcome to {dashboard?.profile?.company_name || 'Your Company'}!
          </h1>
          <p className="mt-1 text-gray-500 font-source">
            Complete your onboarding to get started with your Smart Assistant™.
          </p>
        </div>

        {/* Company info card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-cleona-100 rounded-xl flex items-center justify-center">
                <span className="text-cleona-700 font-bold text-lg">
                  {(dashboard?.profile?.company_name || 'D')[0]}
                </span>
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900 font-arsenal italic">
                  {dashboard?.profile?.company_name || 'DesignHub'}
                </h2>
                <p className="text-sm text-gray-500">
                  Contact Information: {dashboard?.profile?.contact_email || 'admin@company.com'}
                </p>
              </div>
            </div>
            {!dashboard?.profile?.onboarding_completed && (
              <Link
                to="/admin/profile"
                className="px-6 py-2.5 bg-cleona-600 text-white text-sm font-medium rounded-lg hover:bg-cleona-700 transition-colors"
              >
                Complete Setup
              </Link>
            )}
          </div>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat) => (
            <div key={stat.name} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <div className={`w-10 h-10 ${stat.bgColor} rounded-xl flex items-center justify-center mb-4`}>
                <stat.icon className={`w-5 h-5 ${stat.iconColor}`} />
              </div>
              <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              <p className="text-sm text-gray-500">{stat.name}</p>
            </div>
          ))}
        </div>

        {/* Recent sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Documents */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900 font-arsenal">Recent Documents</h3>
            </div>
            <div className="px-6 pb-4">
              {recentDocuments.length > 0 ? (
                <div className="space-y-3">
                  {recentDocuments.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between py-2">
                      <div className="flex items-center gap-3">
                        <PdfIcon />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{doc.filename}</p>
                          <p className="text-xs text-gray-400">
                            {doc.file_type?.toUpperCase()} • {Math.round(doc.file_size / 1024)}KB
                          </p>
                        </div>
                      </div>
                      <span className="text-xs text-cleona-600 font-medium">
                        {doc.status === 'completed' ? 'Completed' : doc.status}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-8 text-sm">No documents uploaded yet.</p>
              )}
            </div>
            <div className="px-4 pb-4">
              <Link
                to="/admin/documents"
                className="flex items-center justify-center gap-2 w-full py-3 bg-cleona-600 text-white text-sm font-medium rounded-xl hover:bg-cleona-700 transition-colors"
              >
                View all documents
                <ArrowRightIcon className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Recent Chat Sessions */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900 font-arsenal">Recent Chat Sessions</h3>
            </div>
            <div className="px-6 pb-4">
              {recentSessions.length > 0 ? (
                <div className="space-y-3">
                  {recentSessions.map((session, index) => (
                    <div key={session.id || index} className="flex items-center justify-between py-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{session.user_name || 'Anonymous'}</p>
                        <p className="text-xs text-gray-400">{session.message_count || 2} messages</p>
                      </div>
                      <span className="text-xs text-cleona-600">
                        {session.created_at ? new Date(session.created_at).toLocaleDateString() : 'Yesterday'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-8 text-sm">No chat sessions yet.</p>
              )}
            </div>
            <div className="px-4 pb-4">
              <Link
                to="/admin/chat"
                className="flex items-center justify-center gap-2 w-full py-3 bg-cleona-600 text-white text-sm font-medium rounded-xl hover:bg-cleona-700 transition-colors"
              >
                View all sessions
                <ArrowRightIcon className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions Sidebar */}
      <div className="hidden xl:block w-72 space-y-4">
        <button
          onClick={() => setQuickActionsOpen(!quickActionsOpen)}
          className="flex items-center justify-between w-full text-left"
        >
          <h3 className="text-lg font-semibold text-gray-900 font-arsenal">Quick Actions</h3>
          <svg
            className={`w-5 h-5 text-gray-500 transition-transform ${quickActionsOpen ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {quickActionsOpen && <>
        {/* Upload Document */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 border-l-4 border-l-amber-400">
          <h4 className="font-semibold text-gray-900 mb-1">Upload Document</h4>
          <p className="text-sm text-gray-500 mb-3">Add new content to your knowledge base</p>
          <Link to="/admin/documents" className="text-cleona-600 text-sm font-medium flex items-center gap-1 hover:text-cleona-700">
            Upload Now <ArrowRightIcon className="w-4 h-4" />
          </Link>
        </div>

        {/* Test Chat */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 border-l-4 border-l-amber-400">
          <h4 className="font-semibold text-gray-900 mb-1">Test Chat</h4>
          <p className="text-sm text-gray-500 mb-3">Try out your AI assistant</p>
          <Link to="/admin/chat" className="text-cleona-600 text-sm font-medium flex items-center gap-1 hover:text-cleona-700">
            Start Chatting <ArrowRightIcon className="w-4 h-4" />
          </Link>
        </div>

        {/* Configure Widget */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 border-l-4 border-l-amber-400">
          <h4 className="font-semibold text-gray-900 mb-1">Configure Website Chat</h4>
          <p className="text-sm text-gray-500 mb-3">Customization your Embeddable chat</p>
          <Link to="/admin/widgets" className="text-cleona-600 text-sm font-medium flex items-center gap-1 hover:text-cleona-700">
            Configure Website Chat <ArrowRightIcon className="w-4 h-4" />
          </Link>
        </div>

        {/* Widget Integration */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 border-l-4 border-l-amber-400">
          <h4 className="font-semibold text-gray-900 mb-1">Website Chat Integration</h4>
          <p className="text-sm text-gray-500 mb-3">Customization your Embeddable chat</p>
          <Link to="/admin/widgets" className="text-cleona-600 text-sm font-medium flex items-center gap-1 hover:text-cleona-700">
            View Website Chat Preview <ArrowRightIcon className="w-4 h-4" />
          </Link>
        </div>

        {/* Current Plan */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 text-center">
          <div className="w-16 h-16 mx-auto mb-3 bg-cleona-50 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-cleona-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
            </svg>
          </div>
          <h4 className="font-semibold text-gray-900 mb-1">Current Plan</h4>
          <Link to="/admin/billing" className="block text-sm text-cleona-600 hover:underline cursor-pointer mb-4">
            You are on the Starter Plan right now.
          </Link>
          <Link
            to="/admin/billing"
            className="block w-full py-2.5 bg-cleona-600 text-white text-sm font-medium rounded-lg hover:bg-cleona-700 transition-colors"
          >
            Get Pro
          </Link>
        </div>
        </>}
      </div>
    </div>
  );
}
